import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { ItemsProps, ItemsFilterType } from "../../types/item";
import { formatSize } from "../../lib/utils";

type MaterialOptionProps = {
  value: number;
  label: string;
};

type MaterialSizeOptionProps = {
  value: number;
  label: string;
  material_id: number;
  is_default: boolean;
};

export interface ItemsState {
  items: ItemsProps[];
  totalItems: number;
  filter: ItemsFilterType;
  isItemModalOpen: boolean;
  editItem: ItemsProps | null;
  // Cascading picker for the Item master's own "Material" field: pick a
  // Material first, then a Material Size scoped to it.
  materialOptions: MaterialOptionProps[];
  materialSizeOptions: MaterialSizeOptionProps[];
  loading: boolean;
  error: string | null;
}

const initialState: ItemsState = {
  items: [],
  totalItems: 0,
  filter: {
    search: null,
    sortBy: "id",
    sortOrder: "desc",
  },
  isItemModalOpen: false,
  editItem: null,
  materialOptions: [],
  materialSizeOptions: [],
  loading: false,
  error: null,
};

type FetchItemsListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  sortBy?: string;
  sortOrder?: string;
};

export const fetchItemsListData = createAsyncThunk(
  "items/fetchList",
  async (req: FetchItemsListDataProps) => {
    let response: any;
    await GET(`/v1/items`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchItemMaterialOptions = createAsyncThunk(
  "items/fetchMaterialOptions",
  async () => {
    let response: any;
    await GET(`/v1/materials-options`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchItemMaterialSizeOptions = createAsyncThunk(
  "items/fetchMaterialSizeOptions",
  async (materialId?: number) => {
    let response: any;
    await GET(`/v1/material-sizes-options`, materialId ? { material_id: materialId } : {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const itemsSlice = createSlice({
  name: "items",
  initialState,
  reducers: {
    toggleItemModal: (state, action: PayloadAction<boolean>) => {
      state.isItemModalOpen = action.payload;
    },
    setEditItem: (state, action: PayloadAction<ItemsProps | null>) => {
      state.editItem = action.payload;
    },
    setItemsFilter: (state, action: PayloadAction<Partial<ItemsFilterType>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchItemsListData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchItemsListData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.items = action.payload.data.data.records;
        state.totalItems = action.payload.data.data.total_records;
        state.error = null;
      } else {
        state.items = [];
        state.error =
          action?.payload?.data?.message ||
          "Something went wrong. Please try again.";
      }
    });
    builder.addCase(fetchItemsListData.rejected, (state, action: any) => {
      state.items = [];
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "Something went wrong. Please try again.";
    });
    builder.addCase(fetchItemMaterialOptions.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.materialOptions = (action.payload.data.data || []).map(
          (m: any) => ({
            value: m.id,
            label: m.name,
          })
        );
      }
    });
    builder.addCase(fetchItemMaterialSizeOptions.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.materialSizeOptions = (action.payload.data.data || []).map(
          (s: any) => ({
            value: s.id,
            label: formatSize(s.size),
            material_id: s.material_id,
            is_default: !!s.is_default,
          })
        );
      }
    });
  },
});

export const { toggleItemModal, setEditItem, setItemsFilter } = itemsSlice.actions;

export const getItems = (state: RootState) => state.items.items;
export const getTotalItems = (state: RootState) => state.items.totalItems;
export const getItemsFilter = (state: RootState) => state.items.filter;
export const getIsItemModalOpen = (state: RootState) => state.items.isItemModalOpen;
export const getEditItem = (state: RootState) => state.items.editItem;
export const getItemMaterialOptions = (state: RootState) =>
  state.items.materialOptions;
export const getItemMaterialSizeOptions = (state: RootState) =>
  state.items.materialSizeOptions;
export const getItemsLoading = (state: RootState) => state.items.loading;

export default itemsSlice.reducer;
